import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { RESTORATION_STORY } from "@/lib/site";
import useMediaQuery from "@/hooks/useMediaQuery";
import useNearViewport from "@/hooks/useNearViewport";
import StoryScene from "./StoryScene";
import StoryPanel from "./StoryPanel";
import StoryProgress from "./StoryProgress";
import StoryTimeline from "./StoryTimeline";

function StoryPinned({ stages }) {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const armed = useNearViewport(ref);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const hint = useTransform(scrollYProgress, [0, 0.06], [1, 0]);

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const i = Math.min(stages.length - 1, Math.max(0, Math.floor(v * stages.length)));
    setActive((prev) => (prev === i ? prev : i));
  });

  return (
    <section ref={ref} className="story-section text-white" data-testid="restoration-story" data-variant="pinned" data-active-stage={active + 1}>
      <div className="story-sticky">
        <StoryScene progress={scrollYProgress} stages={stages} armed={armed} activeIndex={active} />

        <div className="relative h-full container-gaza flex flex-col">
          <div className="pt-10 flex items-center justify-between">
            <div className="live-pill"><span className="dot"></span> The restoration journey</div>
            <div className="hidden md:block text-[11px] uppercase tracking-[0.3em] font-extrabold text-white/50">From flood to back home</div>
          </div>
          <div className="relative flex-1 min-h-[420px] mb-16 lg:mb-20">
            {stages.map((st, i) => (
              <StoryPanel key={st.key} progress={scrollYProgress} stage={st} index={i} total={stages.length} active={i === active} />
            ))}
          </div>
        </div>

        <div className="absolute right-6 xl:right-12 top-1/2 -translate-y-1/2">
          <StoryProgress progress={scrollYProgress} stages={stages} activeIndex={active} />
        </div>

        <motion.div style={{ opacity: hint }} className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-extrabold text-white/55" data-testid="story-scroll-hint">
          Scroll to restore <ChevronDown className="w-4 h-4 animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
}

export default function RestorationStory() {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  if (!isDesktop || reduceMotion) return <StoryTimeline stages={RESTORATION_STORY} />;
  return <StoryPinned stages={RESTORATION_STORY} />;
}
